import {parseCSV} from "./csv-parser";
import {FormattedCameraRow} from './csv-parser';
import {addAsset} from "./redux/assetsSlice";
import {addFolder} from './redux/foldersSlice';
import {fsPromises, dialog, app} from "./electron-modules";
import path from 'path';
import slash from 'slash';
import { hideBackdrop, showBackdrop } from "./app";
import { engineMessageTypeJSON, PythonExecutable } from "./executable";
import LocalScene from "./viewer/js/projections";

const imageListFormats = ['csv', 'txt'];
//const imageFileFormats = ['jpg', 'png'];
const pointCloudFileFormats = ['las', 'idx'];
//const allFileFormats = [...pointCloudFileFormats, ...imageFileFormats];
const imageCSVFilter = [{name: 'Image CSV', extensions: imageListFormats}];

export const isImageCSV = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imageListFormats.indexOf(extension) !== -1;
};

export const isPointCloudFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return pointCloudFileFormats.indexOf(extension) !== -1;
};

export const addAssetFolder = async (showMessage, dispatch, paths, folderID = null) => {
  const cloudPaths = paths.filter(file => isPointCloudFile(file));
  const csvPaths = paths.filter(file => isImageCSV(file));

  if (cloudPaths.length === 0 && csvPaths.length === 0) {
    return;
  }

  if (!folderID) {
    // No folder has been provided, so a new folder is created instead
    const action = dispatch(addFolder("New Folder"));
    folderID = action.payload.id;
  }

  // Add all camera csvs
  const imageFilesAdded = await addCameraFiles(showMessage, dispatch, csvPaths, folderID);

  // Add all pointclouds
  const pointsAdded = addPointCloudFiles(dispatch, cloudPaths, folderID);

  const totalAdded = pointsAdded + imageFilesAdded;
  if (totalAdded === 0) {
    return;
  }

  let addedPlural = totalAdded === 1 ? "" : "s";
  showMessage(`Added [${totalAdded}] file${addedPlural} to project`, {
    variant: "success"
  });
};

export const addPointCloudFiles = (dispatch, assetPaths, folderID) => {
  assetPaths.forEach(pointPath => {
    dispatch(addAsset(folderID, pointPath));
  });

  return assetPaths.length;
};

export const addCameraFiles = async (showMessage, dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;
  for (const csvPath of assetPaths) {
    const added = await addCameraFile(dispatch, csvPath, folderID);
    if (added) {
      numAdded += 1;
    } else {
      showMessage(`Error loading camera file: ${path.basename(csvPath)}`, {
        variant: "error"
      });
    }
  }

  hideBackdrop();

  return numAdded;
};

const addCameraFile = (dispatch, assetPath, folderID): Promise<any> => {
  return new Promise(resolve => {
    const userPath = app.getPath('userData');
    const exe = new PythonExecutable();

    let isConverted = false;
    const inputPath = assetPath;
    const outputPath = path.join(userPath, "inputCsvTemp.csv");
    const projection = LocalScene.engineStyleProjection(LocalScene.dataProjection);

    let commands = [
      "-p", "convert_encompass_csv",
      "--input_file", inputPath,
      "--output_file", outputPath,
      "--projection", projection
    ];

    exe.run({
      command: commands,
      onLine: (messageType, data) => {
        if (messageType === engineMessageTypeJSON) {
          try {
            let response = JSON.parse(data);
            isConverted = response ? true : false;
          } catch (err) {
            // Nothing
          }
        }
      },
      onClose: () => {
        if (!isConverted) {
          resolve(false);
          return;
        }

        // Read converted CSV. assetPath is used to determine the camera
        // location and outputPath is used to read the csv information
        parseCSV(outputPath, assetPath).then(cameras => {
          if (cameras.length === 0) {
            resolve(false);
            return;
          }

          dispatch(addAsset(folderID, assetPath, cameras));
          resolve(true);
        });
      }
    });
  });
};

export const openDialogPoints = (): Promise<string[]> => {
  return dialog.showOpenDialog( {
    filters: [
      {name: 'Point Clouds', extensions: pointCloudFileFormats},
    ],
    properties: ['openFile', 'multiSelections']
  }).then(result => {
    if (result.canceled) {
      return [];
    }

    const filePaths = result.filePaths.map(path => slash(path));
    return filePaths;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return [];
  });
};

export const openDialogFolder = () => {
  return dialog.showOpenDialog({
    properties: ['openDirectory']
  }).then(result => {
    const filePath = slash(result.filePaths[0]);

    if (result.canceled) {
      return {
        directoryPath: filePath,
        assetPaths: Promise.resolve([])
      };
    }

    return {
      directoryPath: filePath,
      assetPaths: parseDirectory(filePath)
    };
  });
};

export const openDialogCSV = (): Promise<FormattedCameraRow[]> => {
  return dialog.showOpenDialog( {
    filters: imageCSVFilter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return null;
  });
};

export const exportDialogCSV = (filename): Promise<FormattedCameraRow[]> => {
  return dialog.showSaveDialog( {
    defaultPath: `${filename}.csv`,
    filters: imageCSVFilter
  }).then(result => {
    if (result.canceled) {
      return;
    }

    const savePath = slash(result.filePath);
    return savePath;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while exporting the specified file.');
    return null;
  });
};

export const openDirectory = (): Promise<any> => {
  return dialog.showOpenDialog({
    properties: ["openDirectory"]
  }).then(result => {
    if (result.canceled) {
      return null;
    }
    if (result.filePaths) {
      const outputFolder = slash(result.filePaths[0]);
      return outputFolder;
    }

    return null;
  });
};

const parseDirectory = async (directoryPath: string): Promise<string[]> => {
  const entries = await fsPromises.readdir(directoryPath, {withFileTypes: true});

  const files = entries
    .filter(entry => entry.isFile())
    .map(file => path.join(directoryPath, file.name));

  const directories = entries
    .filter(entry => entry.isDirectory())
    .map(directory => path.join(directoryPath, directory.name));

  for (let folder of directories) {
    files.push(...await parseDirectory(folder));
  }

  return files;
};
